import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "react-jss";
import useStyles from "./OpenEndRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import InputBox from "../../shared/InputBox";
import {
    getAriaLabelledByFromId,
    getStandaloneInputAriaLabelledBy,
    useAriaLabelledByForQuestionOpenEndChoiceBox,
} from "../../../utils/WCAGHelper";

import { StoreState } from "../../../models/states/StoreState";
import { DefaultFocusStatus } from "../../../models/states/DefaultFocusStatus";
import { isGrid, QuestionType } from "../../../models/enums/QuestionType";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import type { Theme } from "../../../types/Theme";
import type { VariableModel } from "../../../models/VariableModel";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { ButtonChoiceNavigation } from "../../controls/KeyboardNavigation/ButtonChoiceNavigation";
import { ChoiceGridRendererNavigation } from "../../controls/KeyboardNavigation/ChoiceGridRendererNavigation";

interface Props extends RendererProps {
    value?: string;
    name?: string;
    label?: string;
    isDisabled?: boolean;
    isDisabledRequest?: boolean;
    isHidden?: boolean;
    onOpenEndChange?: React.ChangeEventHandler;
    onOpenEndBlur?: React.FocusEventHandler;
    onOpenEndPaste?: (pastedText: string) => void;
    openEndChoiceId?: string;
    openEndType?: string;
    validationResult?: ValidationResultModel;
    size?: string;
    lines?: number;
    style?: any;
    isMouseDownEventPropagation?: boolean;
    isClickEventPropagation?: boolean;
    /** When set (grid row open-ends), register under the row answer variable so Tab walks the OE with that row's cells. */
    focusVariableId?: number;
    /** Hybrid Grid row open-ends: place/register by rowIndex (cells use one variableId per column). */
    focusRowIndex?: number;
}

export default function OpenEndRenderer(props: Props) {
    const {
        isDisabledRequest,
        isDisabled,
        isHidden,
        onOpenEndBlur,
        onOpenEndChange,
        onOpenEndPaste,
        name,
        value,
        label,
        language,
        size,
        lines,
        gridRendering,
        style,
        isMouseDownEventPropagation = false,
        isClickEventPropagation = false,
        question,
        isRowHeader,
        gridRenderedRowIndex,
        gridRenderedColumnIndex,
        focusVariableId,
        focusRowIndex,
    } = props;
    const theme = useTheme() as Theme;
    const useModernTheme = shouldUseModernTheme(theme);
    const classes = useStyles({ theme, size, gridRendering, useModernTheme });
    const useAriaLabelledBy = useAriaLabelledByForQuestionOpenEndChoiceBox(question);
    const defaultFocusStatus = useSelector(
        (state: StoreState) => state.defaultFocusStatus,
    ) as DefaultFocusStatus;

    let resolvedVariableId: number | undefined;
    for (const variable of question?.variables ?? []) {
        if (variable.elementId === name || variable.name === name) {
            resolvedVariableId = variable.id;
            break;
        }
        if (variable.choiceList?.some((choice) => choice.elementId === name)) {
            resolvedVariableId = variable.id;
            break;
        }
    }
    // Grid row OEs pass focusVariableId (row answer variable). List choice OEs resolve via
    // choiceList match above. Prefer focusVariableId so Tab stays within the row.
    const focusEntryVariableId = focusVariableId ?? resolvedVariableId;

    /* Hybrid row OEs receive the HybridGrid question itself (no cell parent). Cells use
     * questionForCell with parent=HybridGrid + rowIndex so isHybridGrid is true. Mirror that
     * shape so Tab from the OE uses Hybrid helpers (then findNextCellWithinSameGrid) instead
     * of ChoiceGrid same-variableId walks that skip other columns in the row. */
    const navigationQuestion =
        gridRendering &&
        focusRowIndex != null &&
        question?.type === QuestionType.HybridGrid
            ? { ...question, parent: question, rowIndex: focusRowIndex }
            : question;

    /* The textarea is not natively reachable in the custom focus order — every neighbour is
     * focused via preventDefault + elementRef.focus(), so leaving the OE must go through the
     * same navigator or Tab falls back to native DOM order and diverges. Only Tab/Shift+Tab
     * are forwarded below; arrows stay native so the caret keeps moving inside the text. */
    const { elementReference: openEndReference, onKeyDown: onNavigationKeyDown } =
        useKeyboardNavigation(
            `${name}_OE_INPUT`,
            focusEntryVariableId != null
                ? ({ id: focusEntryVariableId } as VariableModel)
                : undefined,
            navigationQuestion,
            gridRendering ? ChoiceGridRendererNavigation : ButtonChoiceNavigation,
            null,
        );

    useEffect(() => {
        if (!name || !defaultFocusStatus?.statuses || !question) {
            return;
        }

        let cancelled = false;

        const registerOpenEndInFocusOrder = () => {
            if (cancelled) {
                return;
            }
            const statuses = defaultFocusStatus.statuses;
            const newElementId = `${name}_OE_INPUT`;
            const id = focusEntryVariableId;
            if (!id) {
                return;
            }
            // Remove only this open-end's prior entry. Wiping every *_OE_INPUT for the variable
            // left only the last-mounted choice/row OE in the Tab order.
            for (let i = statuses.length - 1; i >= 0; i--) {
                if (statuses[i].elementId === newElementId) {
                    statuses.splice(i, 1);
                }
            }

            let insertIndex = 0;
            /* Index 0 is a legitimate slot (a column open-end leads the question), so the
             * question-end fallback below must key off "no branch placed it", not off 0. */
            let placedExplicitly = false;

            /* Matches useKeyboardNavigation getQuestionId(): grid rows use parent question id. */
            const keyboardNavQuestionId =
                navigationQuestion?.parent && isGrid(navigationQuestion.parent.type)
                    ? navigationQuestion.parent.id
                    : question.id;

            /* DEV-98226: a choice-level open-end textarea lives inside its owning choice's box,
             * so its focus-order slot must be right after that choice — not after the variable's
             * last choice — or the custom Tab order and the DOM order disagree (Tab from the
             * selected choice skips its own input; Shift+Tab from the next question/nav bar lands
             * on the textarea instead of the last choice). */
            const owningChoiceIndex = !gridRendering
                ? statuses.findIndex(
                      (status) =>
                          status.variableId === id &&
                          // Image choices register their focus entry under the bare
                          // choice.elementId (ImageChoiceRenderer), unlike ButtonChoice which
                          // uses `${id}_INPUT`. Match both so the open-end lands right after
                          // its owning image choice instead of at the end of the group. (DEV-98343)
                          (status.elementId === `${name}_INPUT` || status.elementId === name),
                  )
                : -1;
            if (owningChoiceIndex !== -1) {
                insertIndex = owningChoiceIndex + 1;
                placedExplicitly = true;
            } else if (gridRendering && focusRowIndex != null && keyboardNavQuestionId != null) {
                /* Hybrid: one variableId per column — place OE before the first cell of this
                 * rowIndex so order matches the label-left DOM (OE → col0 → col1 → …). */
                for (let i = 0; i < statuses.length; i++) {
                    if (
                        statuses[i].questionId === keyboardNavQuestionId &&
                        statuses[i].rowIndex === focusRowIndex
                    ) {
                        insertIndex = i;
                        placedExplicitly = true;
                        break;
                    }
                }
            }
            if (!placedExplicitly && gridRendering && focusVariableId != null) {
                /* ChoiceGrid (and Hybrid fallback): a row open-end renders inside the row's
                 * label cell (left side), so its focus slot precedes the row's first cell. */
                for (let i = 0; i < statuses.length; i++) {
                    if (statuses[i].variableId === focusVariableId) {
                        insertIndex = i;
                        placedExplicitly = true;
                        break;
                    }
                }
            }
            if (!placedExplicitly && gridRendering && !isRowHeader && keyboardNavQuestionId != null) {
                /* Column (choice-header) open-ends render above the first data row, so their
                 * focus slot precedes the first cell — after header OEs of earlier columns,
                 * which sit as consecutive *_OE_INPUT entries at the question start. */
                const firstQuestionEntryIndex = statuses.findIndex(
                    (status) => status.questionId === keyboardNavQuestionId,
                );
                if (firstQuestionEntryIndex !== -1) {
                    insertIndex = firstQuestionEntryIndex;
                    while (
                        insertIndex < statuses.length &&
                        statuses[insertIndex].questionId === keyboardNavQuestionId &&
                        statuses[insertIndex].elementId.endsWith("_OE_INPUT")
                    ) {
                        insertIndex++;
                    }
                    placedExplicitly = true;
                }
            }
            if (!placedExplicitly) {
                for (let i = statuses.length - 1; i >= 0; i--) {
                    const currentElementVariableId = statuses[i].variableId;
                    if (currentElementVariableId === id) {
                        insertIndex = i + 1;
                        placedExplicitly = true;
                        break;
                    }
                }
            }

            /* Feedback-only variables (e.g. NPS open end) are not registered until this effect runs,
             * so no prior entry shares variableId and insertIndex stays 0 — which places the textarea
             * before the scale in the focus-status list. NetPromoter keyboard nav then treats Shift+Tab
             * from the last radio as “previous different variable” and jumps to the textarea, trapping
             * focus between the two. Insert after the last element for this question instead.
             * Defer to a microtask so sibling useEffects (scale, grid cells) run first in the same commit. */
            if (!placedExplicitly && statuses.length > 0 && keyboardNavQuestionId != null) {
                for (let i = statuses.length - 1; i >= 0; i--) {
                    if (statuses[i].questionId === keyboardNavQuestionId) {
                        insertIndex = i + 1;
                        break;
                    }
                }
            }

            statuses.splice(insertIndex, 0, {
                elementId: newElementId,
                elementRef: openEndReference,
                variableId: id,
                questionId: keyboardNavQuestionId ?? undefined,
                rowIndex: focusRowIndex,
                focusStatus: false,
            });
        };

        queueMicrotask(registerOpenEndInFocusOrder);

        return () => {
            cancelled = true;
        };
    }, [name, question, focusVariableId, focusRowIndex, focusEntryVariableId, gridRendering, isRowHeader]);

    if (isHidden && !isDisabledRequest) return null;

    let gridTypeLeftLabelOpenEndId: any;
    const parentQuestionType = question?.parent?.type ?? question?.type;
    const isRowOfGrid = gridRendering && isRowHeader;

    if (isRowOfGrid) {
        gridTypeLeftLabelOpenEndId = question?.parent?.elementId ?? question?.elementId;
    }

    return (
        // Wrapper to keep open ends text color when rendering open end inside choices
        <div className={classes.openEnd}>
            <InputBox freeform size="Full" disableTextareaResize={useModernTheme}>
                <textarea
                    id={`${name}_OE_INPUT`}
                    name={`${name}_OE`}
                    ref={openEndReference}
                    {...(useAriaLabelledBy
                        ? {
                            // DEV-98353: standalone open-end needs TITLE + LABEL. Other-specify
                            // boxes nested in Radio/CheckBox are non-grid too, so the helper's own
                            // type+no-parent self-guard (not !gridRendering alone) is what keeps
                            // this scoped to genuine top-level OpenEndAnswer questions.
                            "aria-labelledby":
                                (!gridRendering &&
                                    getStandaloneInputAriaLabelledBy(question, language)) ||
                                getAriaLabelledByFromId(
                                    question,
                                    gridTypeLeftLabelOpenEndId ?? name,
                                    isRowOfGrid ? `${parentQuestionType}_OPENENDROWHEADER` : "",
                                    (gridRenderedColumnIndex ?? 0) + 1,
                                    (gridRenderedRowIndex ?? 0) + 1,
                                ),
                        }
                        : { "aria-label": label })}
                    value={value}
                    onChange={onOpenEndChange}
                    onPaste={
                        onOpenEndPaste
                            ? (e) => {
                                  if (e.clipboardData) {
                                      onOpenEndPaste(e.clipboardData.getData("text/plain"));
                                  }
                              }
                            : undefined
                    }
                    rows={lines}
                    onKeyDown={(e) => {
                        // Forward only Tab/Shift+Tab into the custom focus order; every other
                        // key (arrows, space, characters) stays native for caret movement.
                        if (e.key === "Tab") {
                            onNavigationKeyDown(e);
                        }
                    }}
                    onMouseDown={(e) => {
                        if (!isMouseDownEventPropagation) {
                            e.stopPropagation();
                        }
                    }}
                    onClick={(e) => {
                        // eslint-disable-next-line no-undef
                        if (!isClickEventPropagation) {
                            e.stopPropagation();
                        }
                    }}
                    onBlur={onOpenEndBlur}
                    disabled={isDisabled}
                    style={style}
                />
            </InputBox>
        </div>
    );
}
